<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-card-container">
    <oxd-form :loading="isLoading">
      <!-- Orbit HRM: the candidate's name as the title, the stage beside it, and every detail labelled -->
      <div class="orangehrm-candidate-head">
        <div>
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ candidateName }}
          </oxd-text>
          <p class="orangehrm-candidate-sub">
            {{ vacancyName ? vacancyName : 'No vacancy' }}
            <template v-if="hiringManagerName">
              · {{ $t('recruitment.hiring_manager') }}: {{ hiringManagerName }}
            </template>
          </p>
        </div>
        <span
          v-if="recruitmentStatus"
          :class="['orangehrm-stage', `orangehrm-stage--${stageTone}`]"
        >
          {{ recruitmentStatus }}
        </span>
      </div>
      <oxd-divider />
      <dl class="orangehrm-candidate-facts">
        <div>
          <dt>Email</dt>
          <dd>
            <a v-if="candidate.email" :href="`mailto:${candidate.email}`">
              {{ candidate.email }}
            </a>
            <span v-else>—</span>
          </dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>
            <a
              v-if="candidate.contactNumber"
              :href="`tel:${candidate.contactNumber}`"
            >
              {{ candidate.contactNumber }}
            </a>
            <span v-else>—</span>
            <span
              v-if="candidate.contactNumber"
              class="orangehrm-candidate-fact-note"
            >
              {{ phoneCountryText }}
            </span>
          </dd>
        </div>
        <div>
          <dt>Applied</dt>
          <dd>{{ candidate.dateOfApplication || '—' }}</dd>
        </div>
        <div>
          <dt>CV</dt>
          <dd>
            <a v-if="candidate.hasAttachment" :href="resumeUrl">Download</a>
            <span v-else>—</span>
          </dd>
        </div>
      </dl>
      <oxd-divider />
      <div class="orangehrm-recruitment">
        <!-- Orbit HRM: Schedule meeting is the main action; the workflow steps are outlined, not solid -->
        <div class="orangehrm-recruitment-actions">
          <!-- also without a vacancy: the meeting form asks for one -->
          <oxd-button
            label="Schedule meeting"
            display-type="secondary"
            @click="doWorkflow(100)"
          />
          <oxd-button
            v-if="hasWorkflow(3)"
            :label="$t('general.reject')"
            display-type="ghost-danger"
            @click="doWorkflow(3)"
          />
          <oxd-button
            v-if="hasWorkflow(8)"
            :label="$t('recruitment.offer_declined')"
            display-type="ghost-danger"
            @click="doWorkflow(8)"
          />
          <oxd-button
            v-if="hasWorkflow(6)"
            :label="$t('recruitment.mark_interview_failed')"
            display-type="ghost-danger"
            @click="doWorkflow(6)"
          />
          <oxd-button
            v-if="hasWorkflow(2)"
            :label="$t('recruitment.shortlist')"
            display-type="ghost-success"
            @click="doWorkflow(2)"
          />
          <oxd-button
            v-if="hasWorkflow(4)"
            :label="$t('recruitment.schedule_interview')"
            display-type="ghost-success"
            @click="doWorkflow(4)"
          />
          <oxd-button
            v-if="hasWorkflow(5)"
            :label="$t('recruitment.mark_interview_passed')"
            display-type="ghost-success"
            @click="doWorkflow(5)"
          />
          <oxd-button
            v-if="hasWorkflow(7)"
            :label="$t('recruitment.offer_job')"
            display-type="ghost-success"
            @click="doWorkflow(7)"
          />
          <oxd-button
            v-if="hasWorkflow(9)"
            :label="$t('recruitment.hire')"
            display-type="ghost-success"
            @click="doWorkflow(9)"
          />
        </div>
      </div>
    </oxd-form>
  </div>
</template>

<script>
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import {phoneCountry} from '@/orangehrmAnnaPlugin/util/phoneCountry';

export default {
  name: 'RecruitmentStatus',
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    // Orbit HRM: where ANNA's CV review says they live, for a phone without a country code
    cvLocation: {
      type: String,
      default: null,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/recruitment/candidates',
    );
    const {$tEmpName} = useEmployeeNameTranslate();

    return {
      http,
      translateEmpName: $tEmpName,
    };
  },
  data() {
    return {
      isLoading: false,
      statuses: [
        {id: 1, label: this.$t('recruitment.application_initiated')},
        {id: 2, label: this.$t('recruitment.shortlisted')},
        {id: 3, label: this.$t('leave.rejected')},
        {id: 4, label: this.$t('recruitment.interview_scheduled')},
        {id: 5, label: this.$t('recruitment.interview_passed')},
        {id: 6, label: this.$t('recruitment.interview_failed')},
        {id: 7, label: this.$t('recruitment.job_offered')},
        {id: 8, label: this.$t('recruitment.offer_declined')},
        {id: 9, label: this.$t('recruitment.hired')},
      ],
      actions: [],
    };
  },
  computed: {
    // Orbit HRM: the phone's country from its code, else where the CV says they live
    phoneCountryText() {
      const country = phoneCountry(this.candidate.contactNumber);
      if (country) return country;
      return this.cvLocation
        ? `No country code; the CV says ${this.cvLocation}`
        : 'Country: no data';
    },
    recruitmentStatus() {
      return (
        this.statuses.find((item) => item.id === this.candidate.status?.id)
          ?.label || null
      );
    },
    // the stage pill's colour: undecided, moving on, or out
    stageTone() {
      const id = this.candidate.status?.id;
      if ([3, 6, 8].includes(id)) return 'out';
      if ([5, 7, 9].includes(id)) return 'good';
      if ([2, 4].includes(id)) return 'on';
      return 'new';
    },
    candidateName() {
      return `${this.candidate.firstName} ${this.candidate?.middleName || ''} ${
        this.candidate.lastName
      }`;
    },
    resumeUrl() {
      return `${window.appGlobal.baseUrl}/recruitment/viewCandidateAttachment/candidateId/${this.candidate.id}`;
    },
    vacancyName() {
      const {vacancy} = this.candidate;
      if (!vacancy) return null;
      return vacancy.status === false
        ? vacancy.name + ` (${this.$t('general.closed')})`
        : vacancy.name;
    },
    hiringManagerName() {
      return this.candidate.vacancy?.hiringManager
        ? this.translateEmpName(this.candidate.vacancy.hiringManager, {
            includeMiddle: true,
            excludePastEmpTag: false,
          })
        : undefined;
    },
  },
  watch: {
    candidate() {
      this.getAllowedActions();
    },
  },
  beforeMount() {
    this.getAllowedActions();
  },
  methods: {
    hasWorkflow(actionId) {
      return this.actions.findIndex((actions) => actions.id == actionId) > -1;
    },
    doWorkflow(actionId) {
      navigate(
        '/recruitment/changeCandidateVacancyStatus',
        {},
        {
          candidateId: this.candidate?.id,
          selectedAction: actionId,
        },
      );
    },
    getAllowedActions() {
      this.isLoading = true;
      this.http
        .request({
          method: 'GET',
          url: `/api/v2/recruitment/candidates/${this.candidate?.id}/actions/allowed`,
        })
        .then((response) => {
          const {data} = response.data;
          this.actions = [...data];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-candidate-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.orangehrm-candidate-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64728c;
}
.orangehrm-stage {
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  white-space: nowrap;
  &--new {
    background: #eef1f5;
    color: #4a5568;
  }
  &--on {
    background: #eaf2fb;
    color: #1f5fa8;
  }
  &--good {
    background: #e3f3e6;
    color: #2e7d32;
  }
  &--out {
    background: #fde4e1;
    color: #c62828;
  }
}
.orangehrm-candidate-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px 24px;
  margin: 0;
  dt {
    font-size: 12px;
    font-weight: 600;
    color: #64728c;
    margin-bottom: 2px;
  }
  dd {
    margin: 0;
    font-size: 14px;
    color: #12233f;
    overflow-wrap: anywhere;
  }
  .orangehrm-candidate-fact-note {
    display: block;
    font-size: 12px;
    color: #64728c;
  }
  a {
    color: var(--oxd-primary-one-color);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
}
.orangehrm-recruitment {
  display: flex;
  justify-content: flex-end;
  &-actions {
    gap: 0.4rem;
    display: flex;
    flex-wrap: wrap;
    max-width: 120px;
    margin-left: 60px;
    justify-content: flex-end;
    ::v-deep(.oxd-button--medium) {
      width: 100%;
    }
    @include oxd-respond-to('md') {
      margin-left: unset;
      max-width: unset;
      ::v-deep(.oxd-button--medium) {
        width: unset;
      }
    }
  }
}
::v-deep(.oxd-input-group) {
  margin-bottom: 1rem;
  @include oxd-respond-to('md') {
    margin-bottom: 0;
  }
}
</style>
